import { useCallback, useEffect, useRef, useState } from 'react';
import type { JSONContent } from '@tiptap/core';
import { Link } from 'react-router-dom';
import PageEditor from '../components/Editor';
import { SaveStatusIndicator } from '../components/Editor/SaveStatusIndicator';
import { RevisionHistoryPanel } from '../components/Editor/RevisionHistoryPanel';
import { PreviewPane } from '../components/Editor/PreviewPane';
import { ResizeHandle } from '../components/Editor/ResizeHandle';
import { useAutoSave } from '../hooks/useAutoSave';
import { useDebounce } from '../hooks/useDebounce';
import { authHeaders } from '../hooks/useAuth';

const PAGE_SLUG = 'default';
const SPLIT_KEY = 'reporter.previewSplitRatio';
const MIN_PANE_PX = 280;

function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [query]);

  return matches;
}

export default function EditorPage() {
  const { status, scheduleAutoSave, loadDraft } = useAutoSave(PAGE_SLUG);
  const [initialContent, setInitialContent] = useState<JSONContent | null>(null);
  const [draftToast, setDraftToast] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const [rawHtml, setRawHtml] = useState('');
  const previewHtml = useDebounce(rawHtml, 150);

  const [previewCollapsed, setPreviewCollapsed] = useState(false);
  const [splitRatio, setSplitRatio] = useState<number>(() => {
    try {
      const stored = localStorage.getItem(SPLIT_KEY);
      if (stored) {
        const parsed = parseFloat(stored);
        if (!isNaN(parsed) && parsed > 0 && parsed < 1) return parsed;
      }
    } catch {
      // localStorage unavailable
    }
    return 0.5;
  });
  const containerRef = useRef<HTMLDivElement>(null);

  const isMobile = useMediaQuery('(max-width: 768px)');

  useEffect(() => {
    try {
      localStorage.setItem(SPLIT_KEY, String(splitRatio));
    } catch {
      // localStorage may be unavailable
    }
  }, [splitRatio]);

  useEffect(() => {
    const draft = loadDraft();
    if (!draft?.content_json) return;
    setInitialContent(draft.content_json as JSONContent);
    setDraftToast(true);
    const toastTimer = setTimeout(() => setDraftToast(false), 4_000);
    return () => clearTimeout(toastTimer);
  }, [loadDraft]);

  const handleRevert = async (revisionId: string) => {
    const res = await fetch(`/api/v1/pages/${PAGE_SLUG}/revisions/${revisionId}/restore`, {
      method: 'POST',
      headers: { ...authHeaders() },
    });
    if (res.status === 401) {
      localStorage.removeItem('reporter.token');
      window.location.href = '/login';
      return;
    }
    if (!res.ok) return;
    const page = await res.json();
    setInitialContent(page.content_json);
    setShowHistory(false);
  };

  const handleEditorChange = useCallback(
    ({ json, html }: { json: JSONContent; html: string }) => {
      setRawHtml(html);
      scheduleAutoSave({ title: 'Untitled', content_json: json, content_html: html });
    },
    [scheduleAutoSave]
  );

  const handleResize = useCallback((deltaX: number) => {
    if (!containerRef.current) return;
    const totalWidth = containerRef.current.offsetWidth;
    setSplitRatio((prev) => {
      const newEditorPx = prev * totalWidth + deltaX;
      const clamped = Math.max(MIN_PANE_PX, Math.min(newEditorPx, totalWidth - MIN_PANE_PX));
      return clamped / totalWidth;
    });
  }, []);

  const showPreview = !previewCollapsed && !isMobile;

  return (
    <div className="min-h-screen bg-charcoal-gray flex flex-col">
      {/* Top AppBar — Stitch immersive design */}
      <header className="shrink-0 border-b border-border-subtle bg-ink-black flex items-center justify-between px-4 md:px-6 h-12 z-50 gap-4">
        {/* Left — back + story title */}
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <Link
            to="/stories"
            aria-label="Back to stories"
            className="text-on-surface-variant hover:text-primary transition-colors flex-shrink-0"
          >
            <span className="material-symbols-outlined text-[18px]">arrow_back</span>
          </Link>
          <h1 className="font-meta-code text-[13px] text-on-surface-variant truncate">
            Untitled Story
          </h1>
        </div>

        {/* Center — active theme chip */}
        <div className="flex-shrink-0 hidden md:flex items-center gap-1.5 bg-surface-container border border-border-subtle px-3 py-1">
          <span className="w-2 h-2 rounded-full bg-[#E4002B] flex-shrink-0" />
          <span className="font-meta-code text-[11px] text-on-surface-variant uppercase tracking-wider">
            The Economist
          </span>
        </div>

        {/* Right — revisions, save status, publish */}
        <div className="flex items-center gap-3 shrink-0">
          <button
            onClick={() => setShowHistory(true)}
            title="Revision history"
            className="cursor-pointer flex items-center gap-1 font-label-caps text-label-caps text-meta-gray hover:text-on-surface transition-colors hidden md:flex"
          >
            <span className="material-symbols-outlined text-[16px]">history</span>
          </button>
          <SaveStatusIndicator status={status} />
          {!isMobile && previewCollapsed && (
            <button
              onClick={() => setPreviewCollapsed(false)}
              className="cursor-pointer font-label-caps text-label-caps text-meta-gray hover:text-primary transition-colors uppercase text-[10px]"
            >
              Show Preview
            </button>
          )}
          <button
            className="border border-primary text-primary font-label-caps text-[10px] uppercase px-3 py-1 hover:bg-primary hover:text-ink-black transition-colors"
            style={{ borderRadius: 0 }}
          >
            Publish
          </button>
        </div>
      </header>

      {draftToast && (
        <div
          role="alert"
          className="mx-6 mt-3 font-meta-code text-meta-code text-primary bg-surface-container border border-primary/30 px-4 py-2"
        >
          Draft restored from local storage
        </div>
      )}

      <div ref={containerRef} className="flex flex-1 overflow-hidden">
        <div
          style={showPreview ? { width: `${splitRatio * 100}%` } : { width: '100%' }}
          className="flex flex-col overflow-hidden"
        >
          <PageEditor
            initialContent={initialContent}
            onChange={handleEditorChange}
          />
        </div>

        {showPreview && (
          <>
            <ResizeHandle onResize={handleResize} />
            <div
              style={{ width: `${(1 - splitRatio) * 100}%` }}
              className="flex flex-col overflow-hidden"
            >
              <PreviewPane
                html={previewHtml}
                onCollapse={() => setPreviewCollapsed(true)}
              />
            </div>
          </>
        )}
      </div>

      {showHistory && (
        <RevisionHistoryPanel
          pageSlug={PAGE_SLUG}
          onRevert={handleRevert}
          onClose={() => setShowHistory(false)}
        />
      )}
    </div>
  );
}
